import { BaseElement } from 'base';
import { bus } from 'bus';
import { cart } from 'cart';
import { pdpProperties } from 'amplify-bundles';
import { purchaseRequest } from 'purchase-request';
import { SLIDE_CART_TOPIC, CART_DRAWER_ID } from 'slide-cart-topic';

const SELECTION_TOPIC = 'product:selection';
const TOAST_TOPIC = 'toast:raised';
const STATE = '[data-cta-state]';
const READY_BUTTON = '[data-cta-state="ready"] action-button';
const SELECTION_WAIT = 1500;

class InfographicBlockCta extends BaseElement {
  #variantId = null;
  #sectionId = null;
  #request = null;
  #selectionWaiter = null;
  #preOrder = false;
  #state = 'unavailable';

  onConnect() {
    this.#variantId = Number(this.dataset.variantId) || null;
    this.#sectionId = null;
    this.cleanup(() => {
      this.#request?.cancel();
      this.#selectionWaiter?.();
    });
    this.#preOrder = this.dataset.preOrder === 'true';
    this.#state =
      this.q(`${STATE}:not([hidden])`)?.dataset.ctaState ?? 'unavailable';
    this.subscribe(SELECTION_TOPIC, this.#onSelection);
    this.#bindAdd();
  }

  #onSelection(selection) {
    if (!selection) return;
    this.#sectionId = selection.sectionId ?? null;
    this.#variantId = selection.variantId ?? null;
    this.#preOrder = selection.preOrder === true;
    if (selection.analytics)
      this.dataset.analyticsProduct = JSON.stringify(selection.analytics);
    if (!this.#variantId) {
      this.#render('unavailable');
    } else if (selection.available === true) {
      this.#render('ready');
    } else {
      this.#render('sold_out');
    }
    if (this.#sectionId) this.#selectionWaiter?.();
  }

  #render(state) {
    this.#state = state;
    for (const element of this.qa(STATE))
      element.hidden = element.dataset.ctaState !== state;
  }

  async #bindAdd() {
    const button = this.q(READY_BUTTON);
    if (!button) return;
    await customElements.whenDefined(button.localName);
    if (!this.isConnected) return;
    button.run(() => this.#add());
  }

  async #add() {
    const id = this.#variantId;
    if (this.#state !== 'ready' || !id) return;
    const outcome = await this.#purchase();
    if (outcome === 'failed')
      throw new Error('[infographic-block-cta] add to cart failed');
    if (outcome === 'timeout')
      throw new Error('[infographic-block-cta] purchase request timed out');
    if (outcome !== 'unanswered') return;

    try {
      await cart.add({
        id,
        quantity: 1,
        properties: pdpProperties(this.#preOrder)
      });
    } catch (error) {
      if (this.isConnected) this.#raiseError();
      throw error;
    }
    if (this.isConnected) bus.publish(SLIDE_CART_TOPIC, CART_DRAWER_ID);
  }

  async #purchase() {
    const sectionId = this.#sectionId ?? (await this.#nextSelection());
    if (!this.isConnected) return 'cancelled';
    if (!sectionId) return 'unanswered';
    const request = purchaseRequest.send(sectionId);
    this.#request = request;
    try {
      return await request.result;
    } finally {
      if (this.#request === request) this.#request = null;
    }
  }

  #nextSelection() {
    return new Promise(resolve => {
      const timer = setTimeout(() => this.#selectionWaiter?.(), SELECTION_WAIT);
      this.#selectionWaiter = () => {
        clearTimeout(timer);
        this.#selectionWaiter = null;
        resolve(this.#sectionId);
      };
    });
  }

  #raiseError() {
    const text = this.dataset.error;
    if (text) bus.publish(TOAST_TOPIC, { id: crypto.randomUUID(), text });
  }
}

if (!customElements.get('infographic-block-cta'))
  customElements.define('infographic-block-cta', InfographicBlockCta);
